import { useState, useEffect } from 'react';
import { Clock, Moon, Sun, Shield, Zap, X, ChevronDown } from 'lucide-react';

export interface AutonomySchedule {
    enabled: boolean;
    workHoursStart: string;
    workHoursEnd: string;
    timezone: string;
    weekendsAutonomous: boolean;
    manualOverride: 'none' | 'supervised' | 'autonomous'; // Manual override
}

interface AutonomySchedulerProps {
    onScheduleChange?: (schedule: AutonomySchedule) => void;
}

// Get current mode considering manual override
export function getCurrentMode(schedule: AutonomySchedule): 'supervised' | 'autonomous' {
    // Manual override takes precedence
    if (schedule.manualOverride !== 'none') {
        return schedule.manualOverride;
    }

    if (!schedule.enabled) return 'supervised';

    const now = new Date();
    const currentTime = now.getHours() * 60 + now.getMinutes();

    const [startH, startM] = schedule.workHoursStart.split(':').map(Number);
    const [endH, endM] = schedule.workHoursEnd.split(':').map(Number);
    const workStart = startH * 60 + startM;
    const workEnd = endH * 60 + endM;

    const dayOfWeek = now.getDay();
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

    if (isWeekend && schedule.weekendsAutonomous) return 'autonomous';
    if (currentTime < workStart || currentTime >= workEnd) return 'autonomous';

    return 'supervised';
}

const DEFAULT_SCHEDULE: AutonomySchedule = {
    enabled: true,
    workHoursStart: '09:00',
    workHoursEnd: '17:00',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    weekendsAutonomous: true,
    manualOverride: 'none',
};

function formatTime(time: string): string {
    const [h, m] = time.split(':').map(Number);
    const period = h >= 12 ? 'PM' : 'AM';
    const hour = h % 12 || 12;
    return `${hour}:${m.toString().padStart(2, '0')} ${period}`;
}

function Toggle({ enabled, onChange }: { enabled: boolean; onChange: () => void }) {
    return (
        <button
            onClick={onChange}
            className={`relative w-10 h-5 rounded-full transition-colors ${enabled ? 'bg-primary-500' : 'bg-slate-600'
                }`}
        >
            <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${enabled ? 'translate-x-5' : 'translate-x-0.5'
                }`} />
        </button>
    );
}

export function AutonomyScheduler({ onScheduleChange }: AutonomySchedulerProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [schedule, setSchedule] = useState<AutonomySchedule>(() => {
        const saved = localStorage.getItem('zoroagent_autonomy_schedule');
        if (saved) {
            const parsed = JSON.parse(saved);
            // Ensure manualOverride exists for backwards compatibility
            return { ...DEFAULT_SCHEDULE, ...parsed };
        }
        return DEFAULT_SCHEDULE;
    });
    const [currentMode, setCurrentMode] = useState<'supervised' | 'autonomous'>('supervised');

    useEffect(() => {
        const updateMode = () => setCurrentMode(getCurrentMode(schedule));
        updateMode();
        const interval = setInterval(updateMode, 60000);
        return () => clearInterval(interval);
    }, [schedule]);

    useEffect(() => {
        localStorage.setItem('zoroagent_autonomy_schedule', JSON.stringify(schedule));
        onScheduleChange?.(schedule);
    }, [schedule, onScheduleChange]);

    const updateSchedule = (updates: Partial<AutonomySchedule>) => {
        setSchedule(prev => ({ ...prev, ...updates }));
    };

    const timeOptions = [
        '06:00', '07:00', '08:00', '09:00', '10:00', '11:00', '12:00',
        '13:00', '14:00', '15:00', '16:00', '17:00', '18:00', '19:00', '20:00', '21:00', '22:00'
    ];

    return (
        <div className="relative">
            {/* Mode Badge */}
            <button
                onClick={() => setIsOpen(!isOpen)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${currentMode === 'autonomous'
                        ? 'bg-purple-500/20 text-purple-300 border border-purple-500/30 hover:bg-purple-500/30'
                        : 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 hover:bg-emerald-500/30'
                    }`}
            >
                {currentMode === 'autonomous' ? (
                    <><Moon className="w-3.5 h-3.5" /><span>Autonomous</span></>
                ) : (
                    <><Sun className="w-3.5 h-3.5" /><span>Supervised</span></>
                )}
                <ChevronDown className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            {/* Dropdown */}
            {isOpen && (
                <>
                    <div className="fixed inset-0 z-[99]" onClick={() => setIsOpen(false)} />

                    <div className="absolute right-0 top-full mt-2 w-72 bg-slate-900 border border-slate-700/50 rounded-xl shadow-2xl z-[100] overflow-hidden">
                        {/* Header */}
                        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800">
                            <div className="flex items-center gap-2">
                                <Clock className="w-4 h-4 text-slate-400" />
                                <span className="font-medium text-sm text-white">Agent Mode</span>
                            </div>
                            <button onClick={() => setIsOpen(false)} className="p-1 hover:bg-slate-800 rounded">
                                <X className="w-4 h-4 text-slate-500" />
                            </button>
                        </div>

                        <div className="p-3 space-y-2">
                            {/* Mode Selector */}
                            <button
                                onClick={() => updateSchedule({ manualOverride: 'supervised' })}
                                className={`w-full flex items-center gap-3 p-3 rounded-lg transition-all ${currentMode === 'supervised' && schedule.manualOverride === 'supervised'
                                        ? 'bg-emerald-500/20 border border-emerald-500/30'
                                        : 'hover:bg-slate-800 border border-transparent'
                                    }`}
                            >
                                <div className="p-2 rounded-lg bg-emerald-500/20">
                                    <Shield className="w-4 h-4 text-emerald-400" />
                                </div>
                                <div className="text-left">
                                    <div className="text-sm font-medium text-white">Supervised</div>
                                    <div className="text-xs text-slate-500">Approval required for high-risk</div>
                                </div>
                            </button>

                            <button
                                onClick={() => updateSchedule({ manualOverride: 'autonomous' })}
                                className={`w-full flex items-center gap-3 p-3 rounded-lg transition-all ${currentMode === 'autonomous' && schedule.manualOverride === 'autonomous'
                                        ? 'bg-purple-500/20 border border-purple-500/30'
                                        : 'hover:bg-slate-800 border border-transparent'
                                    }`}
                            >
                                <div className="p-2 rounded-lg bg-purple-500/20">
                                    <Zap className="w-4 h-4 text-purple-400" />
                                </div>
                                <div className="text-left">
                                    <div className="text-sm font-medium text-white">Autonomous</div>
                                    <div className="text-xs text-slate-500">Execute all actions automatically</div>
                                </div>
                            </button>

                            <button
                                onClick={() => updateSchedule({ manualOverride: 'none' })}
                                className={`w-full flex items-center gap-3 p-3 rounded-lg transition-all ${schedule.manualOverride === 'none'
                                        ? 'bg-slate-700/50 border border-slate-600'
                                        : 'hover:bg-slate-800 border border-transparent'
                                    }`}
                            >
                                <div className="p-2 rounded-lg bg-slate-700">
                                    <Clock className="w-4 h-4 text-slate-400" />
                                </div>
                                <div className="text-left">
                                    <div className="text-sm font-medium text-white">Use Schedule</div>
                                    <div className="text-xs text-slate-500">Follow time-based schedule</div>
                                </div>
                            </button>
                        </div>

                        {/* Schedule Settings (collapsed when manual override active) */}
                        {schedule.manualOverride === 'none' && (
                            <div className="px-4 pb-4 pt-2 border-t border-slate-800 space-y-3">
                                <div className="flex items-center justify-between">
                                    <span className="text-xs text-slate-400">Schedule</span>
                                    <Toggle enabled={schedule.enabled} onChange={() => updateSchedule({ enabled: !schedule.enabled })} />
                                </div>

                                {schedule.enabled && (
                                    <>
                                        <div className="flex items-center gap-2">
                                            <select
                                                value={schedule.workHoursStart}
                                                onChange={(e) => updateSchedule({ workHoursStart: e.target.value })}
                                                className="flex-1 bg-slate-800 border border-slate-700 rounded-lg px-2 py-1.5 text-xs text-white cursor-pointer focus:outline-none focus:border-primary-500"
                                            >
                                                {timeOptions.map(t => <option key={t} value={t}>{formatTime(t)}</option>)}
                                            </select>
                                            <span className="text-slate-600 text-xs">→</span>
                                            <select
                                                value={schedule.workHoursEnd}
                                                onChange={(e) => updateSchedule({ workHoursEnd: e.target.value })}
                                                className="flex-1 bg-slate-800 border border-slate-700 rounded-lg px-2 py-1.5 text-xs text-white cursor-pointer focus:outline-none focus:border-primary-500"
                                            >
                                                {timeOptions.map(t => <option key={t} value={t}>{formatTime(t)}</option>)}
                                            </select>
                                        </div>

                                        <div className="flex items-center justify-between">
                                            <span className="text-xs text-slate-400">Auto weekends</span>
                                            <Toggle
                                                enabled={schedule.weekendsAutonomous}
                                                onChange={() => updateSchedule({ weekendsAutonomous: !schedule.weekendsAutonomous })}
                                            />
                                        </div>
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}
